import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useAdmin } from '../../hooks/useAdmin'
import { useCompany } from '../../hooks/useCompany'
import { useLogo } from '../../hooks/useLogo'
import { useLogoUpload } from '../../hooks/useLogoUpload'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { supabase } from '../../services/supabase'
import { Company, CompanySettings } from '../../types'
import { ADMIN_DOMAIN, LOGO_OPTIONS, PLATFORM_LOGO } from '../../utils/constants'
import AISettingsPanel from './AISettingsPanel'
import NewsApiPanel from './NewsApiPanel'
import EarlySignalsPanel, { type EarlySignalsAdminSection } from './EarlySignalsPanel'
import DataApiPanel from './DataApiPanel'
import SsoPanel from './SsoPanel'
import CompanyUsersPanel, { fetchCompanyUserCounts } from './CompanyUsersPanel'
import SupplierRiskEmailSettingsFields from './SupplierRiskEmailSettingsFields'
import {
  EMPTY_SUPPLIER_RISK_EMAIL_FORM,
  loadSupplierRiskEmailForm,
  mergeSupplierRiskEmailCustomizations,
  type SupplierRiskEmailFormState,
} from '../../utils/supplierRiskEmailSettings'

type TabGroup = 'users' | 'tenants' | 'early-signals-data' | 'platform-tools'
type TabType =
  | 'subscribers'
  | 'registrations'
  | 'companies'
  | 'company-users'
  | 'company-settings'
  | 'platform-settings'
  | 'platform-sso'
  | 'es-suppliers'
  | 'es-import'
  | 'es-reports'
  | 'es-spend'
  | 'es-data-api'
  | 'ai-settings'
  | 'news-api'

const ES_TAB_TO_SECTION: Record<'es-suppliers' | 'es-import' | 'es-reports' | 'es-spend', EarlySignalsAdminSection> = {
  'es-suppliers': 'suppliers',
  'es-import': 'import',
  'es-reports': 'reports',
  'es-spend': 'spend',
}

const SECTION_TO_ES_TAB: Record<EarlySignalsAdminSection, TabType> = {
  suppliers: 'es-suppliers',
  import: 'es-import',
  reports: 'es-reports',
  spend: 'es-spend',
}

const AdminDashboard = () => {
  const { isAdmin } = useAuth()
  const { 
    subscribers, 
    subscribersLoading, 
    subscribersError, 
    refetchSubscribers,
    pendingRegistrations,
    pendingRegistrationsLoading,
    pendingRegistrationsError,
    refetchPendingRegistrations,
    rejectRegistration,
    rejectRegistrationLoading,
    companies,
    createCompany,
    createCompanyLoading,
    createUserAccountAsync,
    createUserAccountLoading,
  } = useAdmin()
  const [tempPasswords, setTempPasswords] = useState<Record<string, string>>({})
  const [createAccountError, setCreateAccountError] = useState<Record<string, string>>({})
  const { logo, switchLogo, currentLogo } = useLogo()
  const { uploadLogo, deleteLogo, listUploadedLogos, uploading: logoUploading, error: logoError } = useLogoUpload()
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('subscribers')
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false) // For tablet collapse
  const [drawerOpen, setDrawerOpen] = useState(false) // For mobile drawer
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Record<string, string>>({})
  const [newCompanyNames, setNewCompanyNames] = useState<Record<string, string>>({})
  const [uploadedLogos, setUploadedLogos] = useState<string[]>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [adminCompanyExists, setAdminCompanyExists] = useState(false)
  const [newCompanyName, setNewCompanyName] = useState('')
  const [newCompanyDomain, setNewCompanyDomain] = useState('')
  const [companyUsersCompanyId, setCompanyUsersCompanyId] = useState<string | null>(null)
  const [companyUserCounts, setCompanyUserCounts] = useState<Record<string, number>>({})
  
  // Company Settings state
  const { company: contextCompany, settings: contextSettings, refreshCompany } = useCompany()
  const queryClient = useQueryClient()
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null) // null = Platform Settings
  const [companySettingsTab, setCompanySettingsTab] = useState<'theme' | 'content' | 'features'>('theme')
  
  // Theme settings
  const [primaryColor, setPrimaryColor] = useState('#000000')
  const [secondaryColor, setSecondaryColor] = useState('#0066CC')
  const [companyLogo, setCompanyLogo] = useState('logo1.png')
  const [companyLogoFile, setCompanyLogoFile] = useState<File | null>(null)
  const [companyLogoPreviewUrl, setCompanyLogoPreviewUrl] = useState<string | null>(null)
  
  // Content settings
  const [sections, setSections] = useState<Record<string, { enabled: boolean; customText?: string }>>({})
  
  // Feature flags
  const [features, setFeatures] = useState<Record<string, boolean>>({
    newsletter: true,
    adminDashboard: false,
    customReports: false,
    earlySignals: false,
  })
  
  // Early Signals tab visibility
  const [earlySignalsTabs, setEarlySignalsTabs] = useState<Record<string, boolean>>({
    ai: true,
    dashboard: true,
    alerts: false,
    riskMatrix: true,
    mitigation: true,
    specialReports: true,
    executiveSummary: true,
    supplierRiskIntake: false,
    sendRiskAlert: false,
    riskAlerts: true,
    briefs: false,
  })

  const [supplierRiskEmails, setSupplierRiskEmails] = useState<SupplierRiskEmailFormState>(
    EMPTY_SUPPLIER_RISK_EMAIL_FORM,
  )
  
  // Fetch selected company data
  const { data: selectedCompany } = useQuery<Company | null>({
    queryKey: ['company', selectedCompanyId],
    queryFn: async () => {
      if (!selectedCompanyId) return null
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', selectedCompanyId)
        .single()
      if (error) throw error
      return data as Company
    },
    enabled: !!selectedCompanyId && isAdmin,
  })
  
  // Fetch selected company settings
  const { data: selectedCompanySettings } = useQuery<CompanySettings | null>({
    queryKey: ['company_settings', selectedCompanyId],
    queryFn: async () => {
      if (!selectedCompanyId) return null
      const { data, error } = await supabase
        .from('company_settings')
        .select('*')
        .eq('company_id', selectedCompanyId)
        .single()
      if (error && error.code !== 'PGRST116') throw error
      return data as CompanySettings | null
    },
    enabled: !!selectedCompanyId && isAdmin,
  })
  
  // Determine which company and settings to use
  const displayCompany = selectedCompany || contextCompany
  const displaySettings = selectedCompanySettings || contextSettings

  useEffect(() => {
    if (!isAdmin) return

    const handleOpen = () => setIsOpen(true)
    window.addEventListener('openAdminDashboard', handleOpen)
    
    // Handle company selection in Company Settings tab
    const handleSelectCompany = (event: CustomEvent) => {
      const companyId = event.detail?.companyId || null
      if (companyId) {
        setActiveTab('company-settings')
        setSelectedCompanyId(companyId)
      }
    }
    window.addEventListener('selectCompanyInSettings', handleSelectCompany as EventListener)
    
    return () => {
      window.removeEventListener('openAdminDashboard', handleOpen)
      window.removeEventListener('selectCompanyInSettings', handleSelectCompany as EventListener)
    }
  }, [isAdmin])

  // Refetch subscribers when dashboard opens so the list is fresh
  useEffect(() => {
    if (isOpen && isAdmin) {
      refetchSubscribers()
    }
  }, [isOpen, isAdmin, refetchSubscribers])

  useEffect(() => {
    if (!isOpen) {
      setDrawerOpen(false)
      setSidebarCollapsed(false)
    }
  }, [isOpen])

  useEffect(() => {
    // Prevent body scroll when dashboard is open
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    if (!drawerOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [drawerOpen])

  // Close drawer when tab changes on mobile
  useEffect(() => {
    if (drawerOpen) {
      setDrawerOpen(false)
    }
  }, [activeTab])

  const loadUploadedLogos = async () => {
    const logos = await listUploadedLogos()
    setUploadedLogos(logos)
  }

  // Load uploaded logos when platform-settings tab is active
  useEffect(() => {
    if (activeTab === 'platform-settings' && isAdmin) {
      loadUploadedLogos()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAdmin])

  // Check if admin company exists
  useEffect(() => {
    if (companies.length > 0) {
      const adminCompany = companies.find(c => c.domain === ADMIN_DOMAIN)
      setAdminCompanyExists(!!adminCompany)
    } else {
      setAdminCompanyExists(false)
    }
  }, [companies])

  // Member counts for Companies table
  useEffect(() => {
    if (!isAdmin || !isOpen) return
    if (activeTab !== 'companies' && activeTab !== 'company-users') return
    let cancelled = false
    fetchCompanyUserCounts()
      .then((counts) => {
        if (!cancelled) setCompanyUserCounts(counts)
      })
      .catch(() => {
        if (!cancelled) setCompanyUserCounts({})
      })
    return () => {
      cancelled = true
    }
  }, [isAdmin, isOpen, activeTab, companies.length])
  
  // Load company settings when they change
  useEffect(() => {
    if (displaySettings && selectedCompanyId) {
      // Load theme config
      if (displaySettings?.theme_config) {
        setPrimaryColor(displaySettings.theme_config.primaryColor || '#000000')
        setSecondaryColor(displaySettings.theme_config.secondaryColor || '#0066CC')
        setCompanyLogo(displaySettings.theme_config.logo || 'logo1.png')
      } else {
        setPrimaryColor('#000000')
        setSecondaryColor('#0066CC')
        setCompanyLogo('logo1.png')
      }
      
      // Load content config
      if (displaySettings?.content_config?.sections) {
        setSections(displaySettings.content_config.sections)
      } else {
        setSections({})
      }
      
      // Load features (platform Admin is email-domain only — never a company flag)
      if (displaySettings?.features) {
        setFeatures({
          newsletter: true,
          customReports: false,
          earlySignals: false,
          ...displaySettings.features,
          adminDashboard: false,
        })
      } else {
        setFeatures({
          newsletter: true,
          adminDashboard: false,
          customReports: false,
          earlySignals: false,
        })
      }
      const es = displaySettings?.customizations?.earlySignals
      if (es?.tabs && typeof es.tabs === 'object') {
        setEarlySignalsTabs(prev => ({ ...prev, ...es.tabs }))
      } else {
        setEarlySignalsTabs({
          ai: true,
          dashboard: true,
          alerts: false,
          riskMatrix: true,
          mitigation: true,
          specialReports: true,
          executiveSummary: true,
          supplierRiskIntake: false,
          sendRiskAlert: false,
          riskAlerts: true,
          briefs: false,
        })
      }
      setSupplierRiskEmails(loadSupplierRiskEmailForm(displaySettings?.customizations))
    } else if (selectedCompanyId && !displaySettings) {
      // Reset to defaults when company changes but no settings exist yet
      setPrimaryColor('#000000')
      setSecondaryColor('#0066CC')
      setCompanyLogo('logo1.png')
      setSections({})
      setFeatures({
        newsletter: true,
        adminDashboard: false,
        customReports: false,
        earlySignals: false,
      })
      setEarlySignalsTabs({
        ai: true,
        dashboard: true,
        alerts: false,
        riskMatrix: true,
        mitigation: true,
        specialReports: true,
        executiveSummary: true,
        supplierRiskIntake: false,
        sendRiskAlert: false,
        riskAlerts: true,
        briefs: false,
      })
      setSupplierRiskEmails(EMPTY_SUPPLIER_RISK_EMAIL_FORM)
    }
  }, [displaySettings, selectedCompanyId])
  
  // Company settings mutation
  const updateCompanySettingsMutation = useMutation({
    mutationFn: async (updates: {
      theme_config?: Record<string, any>
      content_config?: Record<string, any>
      features?: Record<string, boolean>
      customizations?: Record<string, any>
    }) => {
      const companyIdToUse = selectedCompanyId
      if (!companyIdToUse) throw new Error('No company selected')
      
      const { error } = await supabase
        .from('company_settings')
        .upsert({
          company_id: companyIdToUse,
          theme_config: updates.theme_config ?? displaySettings?.theme_config ?? {},
          content_config: updates.content_config ?? displaySettings?.content_config ?? {},
          features: updates.features ?? displaySettings?.features ?? {},
          customizations: updates.customizations ?? displaySettings?.customizations ?? {},
          updated_at: new Date().toISOString(),
        })
      
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company_settings'] })
      queryClient.invalidateQueries({ queryKey: ['company_settings', selectedCompanyId] })
      refreshCompany()
    },
  })
  
  // Company settings handlers
  const handleSaveTheme = () => {
    if (!selectedCompanyId) return
    updateCompanySettingsMutation.mutate({
      theme_config: {
        primaryColor,
        secondaryColor,
        logo: companyLogo,
      },
    })
  }
  
  const handleSaveContent = () => {
    if (!selectedCompanyId) return
    updateCompanySettingsMutation.mutate({
      content_config: {
        sections,
      },
    })
  }
  
  const handleSaveFeatures = () => {
    if (!selectedCompanyId) return
    const withTabs = {
      ...displaySettings?.customizations,
      earlySignals: {
        ...(displaySettings?.customizations?.earlySignals ?? {}),
        tabs: earlySignalsTabs,
      },
    }
    const customizations = mergeSupplierRiskEmailCustomizations(withTabs, supplierRiskEmails)
    updateCompanySettingsMutation.mutate({
      features: { ...features, adminDashboard: false },
      customizations,
    })
  }
  
  const toggleSection = (sectionId: string) => {
    setSections(prev => ({
      ...prev,
      [sectionId]: {
        enabled: prev[sectionId]?.enabled !== false ? false : true,
        customText: prev[sectionId]?.customText,
      },
    }))
  }
  
  const toggleFeature = (featureId: string) => {
    if (featureId === 'adminDashboard') return
    setFeatures(prev => ({
      ...prev,
      [featureId]: !prev[featureId],
    }))
  }
  
  const toggleEarlySignalsTab = (tabId: string) => {
    setEarlySignalsTabs(prev => ({ ...prev, [tabId]: !prev[tabId] }))
  }
  
  const handleCompanyLogoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setCompanyLogoFile(file)
      setCompanyLogoPreviewUrl(URL.createObjectURL(file))
    }
  }
  
  const handleUploadCompanyLogo = async () => {
    if (!companyLogoFile) return
    const url = await uploadLogo(companyLogoFile)
    if (url) {
      setCompanyLogo(url)
      setCompanyLogoFile(null)
      setCompanyLogoPreviewUrl(null)
      const input = document.getElementById('company-logo-upload-input') as HTMLInputElement
      if (input) input.value = ''
    }
  }
  
  const isPredefinedLogo = (v: string) => v.startsWith('logo') && !v.startsWith('http')
  const companyLogoDisplayUrl = isPredefinedLogo(companyLogo) ? `/assets/${companyLogo}` : companyLogo

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      // Create preview URL
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    const logoUrl = await uploadLogo(selectedFile)
    if (logoUrl) {
      // Switch to uploaded logo immediately
      switchLogo(logoUrl)
      // Refresh uploaded logos list
      await loadUploadedLogos()
      // Clear selection
      setSelectedFile(null)
      setPreviewUrl(null)
      // Reset file input
      const fileInput = document.getElementById('logo-upload-input') as HTMLInputElement
      if (fileInput) fileInput.value = ''
    }
  }

  const handleDeleteLogo = async (logoUrl: string) => {
    if (confirm('Are you sure you want to delete this logo?')) {
      const success = await deleteLogo(logoUrl)
      if (success) {
        // If deleted logo was current, switch to default
        if (currentLogo === logoUrl) {
          switchLogo(LOGO_OPTIONS.LOGO1)
        }
        // Refresh list
        await loadUploadedLogos()
      }
    }
  }

  const handleCreateAdminCompany = async () => {
    createCompany(
      { name: 'The Simple Intel', domain: ADMIN_DOMAIN },
      {
        onSuccess: () => {
          alert('Admin company created successfully!')
        },
        onError: (error: any) => {
          alert(`Error creating admin company: ${error.message}`)
        },
      }
    )
  }

  const handleAddCompany = (e: React.FormEvent) => {
    e.preventDefault()
    const name = newCompanyName.trim()
    const domain = newCompanyDomain.trim().toLowerCase()
    if (!name || !domain) return
    createCompany(
      { name, domain },
      {
        onSuccess: () => {
          setNewCompanyName('')
          setNewCompanyDomain('')
        },
        onError: (error: any) => {
          alert(`Error creating company: ${error.message}`)
        },
      }
    )
  }

  const getErrorMessage = (error: unknown) =>
    error != null && typeof error === 'object' && 'message' in error
      ? String((error as { message?: unknown }).message)
      : typeof error === 'string'
        ? error
        : String(error)

  const handleCreateAccount = async (registration: any) => {
    setCreateAccountError(prev => {
      const next = { ...prev }
      delete next[registration.id]
      return next
    })

    let companyId = selectedCompanyIds[registration.id]

    if (!companyId) {
      const existingCompany = companies.find(c => c.domain === registration.company_domain)
      if (existingCompany) {
        companyId = existingCompany.id
      } else {
        const companyName = newCompanyNames[registration.id]?.trim()
        if (!companyName) {
          setCreateAccountError(prev => ({
            ...prev,
            [registration.id]: 'Please select a company from the dropdown or enter a new company name.'
          }))
          return
        }
        createCompany(
          { name: companyName, domain: registration.company_domain },
          {
            onSuccess: async (newCompany) => {
              try {
                const result = await createUserAccountAsync({
                  email: registration.email,
                  name: registration.name,
                  companyId: newCompany.id,
                  registrationId: registration.id,
                  designation: registration.designation ?? null,
                  designation_other: registration.designation_other ?? null,
                })
                setCreateAccountError(prev => {
                  const next = { ...prev }
                  delete next[registration.id]
                  return next
                })
                setTempPasswords(prev => ({ ...prev, [registration.id]: result.tempPassword }))
                setNewCompanyNames(prev => { const u = { ...prev }; delete u[registration.id]; return u })
                setSelectedCompanyIds(prev => { const u = { ...prev }; delete u[registration.id]; return u })
                alert(`Account created! Temporary password: ${result.tempPassword}\n\nPlease send this password to the user.`)
              } catch (error: unknown) {
                const message = getErrorMessage(error)
                setCreateAccountError(prev => ({ ...prev, [registration.id]: message }))
                alert(`Error creating account: ${message}`)
              }
            },
            onError: (error: unknown) => {
              const message = getErrorMessage(error)
              setCreateAccountError(prev => ({ ...prev, [registration.id]: message }))
              alert(`Error creating company: ${message}`)
            },
          }
        )
        return
      }
    }

    try {
      const result = await createUserAccountAsync({
        email: registration.email,
        name: registration.name,
        companyId,
        registrationId: registration.id,
        designation: registration.designation ?? null,
        designation_other: registration.designation_other ?? null,
      })
      setCreateAccountError(prev => {
        const next = { ...prev }
        delete next[registration.id]
        return next
      })
      setTempPasswords(prev => ({ ...prev, [registration.id]: result.tempPassword }))
      setSelectedCompanyIds(prev => { const u = { ...prev }; delete u[registration.id]; return u })
      setNewCompanyNames(prev => { const u = { ...prev }; delete u[registration.id]; return u })
      const emailSent = (result as { emailSent?: boolean }).emailSent
      alert(
        emailSent
          ? `Account created successfully!\n\nAn email with the temporary password has been sent to ${registration.email}.`
          : `Account created successfully!\n\nTemporary password: ${result.tempPassword}\n\nPlease send this password to ${registration.email}`
      )
    } catch (error: unknown) {
      const message = getErrorMessage(error)
      setCreateAccountError(prev => ({ ...prev, [registration.id]: message }))
      alert(`Error creating account: ${message}`)
    }
  }

  const handleRejectRegistration = async (registrationId: string) => {
    if (confirm('Are you sure you want to reject this registration?')) {
      rejectRegistration(registrationId, {
        onError: (error: any) => {
          alert(`Error rejecting registration: ${error.message}`)
        },
      })
    }
  }

  if (!isAdmin || !isOpen) return null

  const tabGroups: { 
    id: TabGroup
    label: string
    tabs: { id: TabType; label: string; icon: string }[]
  }[] = [
    {
      id: 'users',
      label: 'Users',
      tabs: [
        { id: 'subscribers', label: 'Newsletter subscribers', icon: 'fa-users' },
      ]
    },
    {
      id: 'tenants',
      label: 'Tenants',
      tabs: [
        { id: 'companies', label: 'Companies', icon: 'fa-building' },
        { id: 'company-users', label: 'Company users', icon: 'fa-user-group' },
        { id: 'registrations', label: 'Pending registrations', icon: 'fa-clock' },
        { id: 'company-settings', label: 'Company settings', icon: 'fa-cog' },
      ]
    },
    {
      id: 'early-signals-data',
      label: 'Early Signals data',
      tabs: [
        { id: 'es-suppliers', label: 'Suppliers', icon: 'fa-truck-field' },
        { id: 'es-import', label: 'Data import', icon: 'fa-file-excel' },
        { id: 'es-reports', label: 'Special reports', icon: 'fa-file-pdf' },
        { id: 'es-spend', label: 'Spend cube', icon: 'fa-coins' },
        { id: 'es-data-api', label: 'Data API', icon: 'fa-plug' },
      ]
    },
    {
      id: 'platform-tools',
      label: 'Platform tools',
      tabs: [
        { id: 'platform-settings', label: 'Site logos', icon: 'fa-image' },
        { id: 'platform-sso', label: 'Sign-in setup', icon: 'fa-shield-halved' },
        { id: 'ai-settings', label: 'AI settings', icon: 'fa-robot' },
        { id: 'news-api', label: 'News API', icon: 'fa-newspaper' },
      ]
    },
  ]

  return (
    <div className="fixed inset-0 z-[100] bg-white flex overflow-hidden">
      {/* Mobile Overlay */}
      {drawerOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Navigation */}
      {/* Mobile Drawer */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-50 bg-white shadow-xl transform transition-transform duration-300 ease-in-out
          ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:hidden
          w-72 max-w-[85vw]
          flex flex-col
        `}
        aria-label="Admin navigation"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold">Admin dashboard</h2>
          <button
            onClick={() => setDrawerOpen(false)}
            className="text-gray-400 hover:text-black p-1 transition-colors"
            aria-label="Close navigation"
          >
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-4">
          {tabGroups.map((group, groupIndex) => (
            <div key={group.id} className={groupIndex > 0 ? 'mt-6' : ''}>
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {group.label}
              </div>
              <div className="mt-1 space-y-1">
                {group.tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id)
                      setDrawerOpen(false)
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-colors
                      ${activeTab === tab.id
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                    aria-current={activeTab === tab.id ? 'page' : undefined}
                  >
                    <i className={`fa-solid ${tab.icon} w-5 flex-shrink-0`} aria-hidden></i>
                    <span className="truncate">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Desktop/Tablet Sidebar */}
      <aside 
        className={`
          hidden md:flex md:flex-col md:border-r md:border-gray-200 md:bg-gray-50
          transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'md:w-16' : 'md:w-64'}
        `}
        aria-label="Admin navigation"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          {!sidebarCollapsed && (
            <h2 className="text-lg font-bold truncate">Admin dashboard</h2>
          )}
          <div className="flex items-center gap-2">
            {sidebarCollapsed && (
              <h2 className="text-lg font-bold">AD</h2>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="text-gray-400 hover:text-black p-1 transition-colors"
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <i className={`fa-solid ${sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'} text-sm`}></i>
            </button>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-4">
          {tabGroups.map((group, groupIndex) => (
            <div key={group.id} className={groupIndex > 0 ? 'mt-6' : ''}>
              {!sidebarCollapsed && (
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {group.label}
                </div>
              )}
              <div className="mt-1 space-y-1">
                {group.tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-colors
                      ${sidebarCollapsed ? 'justify-center' : ''}
                      ${activeTab === tab.id
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                    aria-current={activeTab === tab.id ? 'page' : undefined}
                    title={sidebarCollapsed ? tab.label : undefined}
                  >
                    <i className={`fa-solid ${tab.icon} w-5 flex-shrink-0`} aria-hidden></i>
                    {!sidebarCollapsed && (
                      <span className="truncate">{tab.label}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 bg-white border-b border-gray-200 z-10 shadow-sm">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <img
                  src={PLATFORM_LOGO}
                  alt="The Simple"
                  className="h-8 sm:h-9 md:h-10 lg:h-12 w-auto flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl font-bold">Admin dashboard</h1>
                  <p className="text-sm text-gray-500 truncate">
                    Users · Tenants · Early Signals data · Platform tools
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setDrawerOpen(true)}
                  className="lg:hidden text-gray-400 hover:text-black p-2 transition-colors"
                  aria-label="Open navigation menu"
                >
                  <i className="fa-solid fa-bars text-xl"></i>
                </button>
                {/* Close Button */}
                <button
                  onClick={() => {
                    setIsOpen(false)
                    window.dispatchEvent(new CustomEvent('closeAdminDashboard'))
                  }}
                  className="text-gray-400 hover:text-black p-2 transition-colors"
                  aria-label="Close dashboard"
                >
                  <i className="fa-solid fa-xmark text-2xl"></i>
                </button>
              </div>
            </div>
          </div>
        </header>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-lg sm:text-xl font-bold">
                  <i className="fa-solid fa-users mr-2"></i>Newsletter subscribers
                </h2>
                <button
                  onClick={() => refetchSubscribers()}
                  className="text-sm text-blue-600 hover:underline font-medium"
                >
                  <i className="fa-solid fa-arrow-rotate-right mr-1"></i>Refresh
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-600 font-semibold">
                      <tr>
                        <th className="p-3 sm:p-4">Email</th>
                        <th className="p-3 sm:p-4 hidden sm:table-cell">Date</th>
                        <th className="p-3 sm:p-4 hidden md:table-cell">Company</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribersLoading ? (
                        <tr>
                          <td colSpan={3} className="p-8 text-center text-gray-400">
                            <i className="fa-solid fa-spinner fa-spin mr-2"></i>Loading...
                          </td>
                        </tr>
                      ) : subscribersError ? (
                        <tr>
                          <td colSpan={3} className="p-6 sm:p-8 text-center text-red-400">
                            <div className="space-y-2">
                              <div className="font-semibold">Error loading subscribers</div>
                              <div className="text-sm break-words">{subscribersError instanceof Error ? subscribersError.message : 'Unknown error'}</div>
                              <div className="text-xs text-gray-500 mt-2">
                                Ensure RLS policies allow reading 'subscribers' for admin users.
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : subscribers.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="p-8 text-center text-gray-400">
                            No subscribers found.
                          </td>
                        </tr>
                      ) : (
                        subscribers.map((sub) => (
                          <tr key={sub.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="p-3 sm:p-4 font-medium break-words">{sub.email}</td>
                            <td className="p-3 sm:p-4 text-gray-500 hidden sm:table-cell">
                              {new Date(sub.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-3 sm:p-4 text-gray-500 text-xs hidden md:table-cell break-words">
                              {sub.company_id ? 'Company ID: ' + sub.company_id.substring(0, 8) + '...' : 'No company'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending Registrations Tab */}
        {activeTab === 'registrations' && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">
                  <i className="fa-solid fa-clock mr-2"></i>Pending Registrations
                </h2>
                <button
                  onClick={() => refetchPendingRegistrations()}
                  className="text-sm text-blue-600 hover:underline font-medium"
                >
                  <i className="fa-solid fa-arrow-rotate-right mr-1"></i>Refresh
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                {pendingRegistrationsLoading ? (
                  <div className="p-8 text-center text-gray-400">
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>Loading...
                  </div>
                ) : pendingRegistrationsError ? (
                  <div className="p-8 text-center text-red-400">
                    <div className="space-y-2">
                      <div className="font-semibold">Error loading pending registrations</div>
                      <div className="text-sm">{pendingRegistrationsError instanceof Error ? pendingRegistrationsError.message : 'Unknown error'}</div>
                    </div>
                  </div>
                ) : pendingRegistrations.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    No pending registrations.
                  </div>
                ) : (
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-600 font-semibold">
                      <tr>
                        <th className="p-4">Name</th>
                        <th className="p-4">Email</th>
                        <th className="p-4">Company Domain</th>
                        <th className="p-4">Designation</th>
                        <th className="p-4">Date</th>
                        <th className="p-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingRegistrations.map((reg) => (
                        <tr key={reg.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="p-4 font-medium">{reg.name}</td>
                          <td className="p-4">{reg.email}</td>
                          <td className="p-4 text-gray-600">{reg.company_domain}</td>
                          <td className="p-4 text-gray-600">
                            {reg.designation
                              ? reg.designation === 'Other' && reg.designation_other
                                ? `Other – ${reg.designation_other}`
                                : reg.designation
                              : '—'}
                          </td>
                          <td className="p-4 text-gray-500">
                            {new Date(reg.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col gap-3 min-w-[280px] sm:min-w-[400px]">
                              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                                <select
                                  value={selectedCompanyIds[reg.id] || ''}
                                  onChange={(e) => {
                                    setSelectedCompanyIds(prev => ({ ...prev, [reg.id]: e.target.value }))
                                    setCreateAccountError(prev => { const u = { ...prev }; delete u[reg.id]; return u })
                                  }}
                                  className="text-sm p-2 border border-gray-300 rounded flex-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <option value="">New Company</option>
                                  {companies.map((company) => (
                                    <option key={company.id} value={company.id}>
                                      {company.name} ({company.domain})
                                    </option>
                                  ))}
                                </select>
                                {(selectedCompanyIds[reg.id] || '') === '' && (
                                  <input
                                    type="text"
                                    value={newCompanyNames[reg.id] || ''}
                                    onChange={(e) => {
                                      setNewCompanyNames(prev => ({ ...prev, [reg.id]: e.target.value }))
                                      setCreateAccountError(prev => { const u = { ...prev }; delete u[reg.id]; return u })
                                    }}
                                    placeholder="Company name"
                                    className="text-sm p-2 border border-gray-300 rounded flex-1"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}
                              </div>
                              <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                  onClick={() => handleCreateAccount(reg)}
                                  disabled={createUserAccountLoading}
                                  className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 font-medium flex-1 sm:flex-none"
                                >
                                  {createUserAccountLoading ? 'Creating...' : 'Create Account'}
                                </button>
                                <button
                                  onClick={() => handleRejectRegistration(reg.id)}
                                  disabled={rejectRegistrationLoading}
                                  className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 font-medium flex-1 sm:flex-none"
                                >
                                  Reject
                                </button>
                              </div>
                              {createAccountError[reg.id] && (
                                <p className="text-red-600 text-sm" role="alert">
                                  {createAccountError[reg.id]}
                                </p>
                              )}
                              {tempPasswords[reg.id] && (
                                <div className="text-sm bg-yellow-50 border border-yellow-200 rounded p-3 break-all">
                                  <strong>Temp Password:</strong> <code className="font-mono bg-yellow-100 px-2 py-1 rounded ml-1 break-all">{tempPasswords[reg.id]}</code>
                                  <div className="text-gray-600 mt-1 text-xs break-words">Send this to {reg.email}</div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-bold">
                <i className="fa-solid fa-building mr-2"></i>Companies
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Companies (and their domains) are the single list for who can register. Add a company to allow that email domain for signup.
              </p>
            </div>
            <div className="p-4 sm:p-6">
              <form onSubmit={handleAddCompany} className="flex flex-col sm:flex-row gap-2 mb-4">
                <input
                  type="text"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  placeholder="Company name"
                  className="flex-1 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none"
                />
                <input
                  type="text"
                  value={newCompanyDomain}
                  onChange={(e) => setNewCompanyDomain(e.target.value)}
                  placeholder="example.com"
                  className="flex-1 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={createCompanyLoading || !newCompanyName.trim() || !newCompanyDomain.trim()}
                  className="px-4 py-3 bg-black text-white rounded-md font-medium hover:bg-gray-800 disabled:opacity-50 whitespace-nowrap"
                >
                  {createCompanyLoading ? 'Adding...' : 'Add company'}
                </button>
              </form>
              {!adminCompanyExists && companies.length >= 0 && (
                <div className="mb-4 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div>
                      <h3 className="font-semibold text-yellow-900">Admin Company Missing</h3>
                      <p className="text-sm text-yellow-700">
                        The admin company ({ADMIN_DOMAIN}) is not in the database. Create it to enable full functionality.
                      </p>
                    </div>
                    <button
                      onClick={handleCreateAdminCompany}
                      disabled={createCompanyLoading}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 font-medium whitespace-nowrap"
                    >
                      {createCompanyLoading ? 'Creating...' : 'Create Admin Company'}
                    </button>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-600 font-semibold">
                      <tr>
                        <th className="p-3 sm:p-4">Name</th>
                        <th className="p-3 sm:p-4">Domain</th>
                        <th className="p-3 sm:p-4">Users</th>
                        <th className="p-3 sm:p-4">Status</th>
                        <th className="p-3 sm:p-4 hidden lg:table-cell">Created</th>
                        <th className="p-3 sm:p-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companies.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-gray-400">
                            No companies found.
                          </td>
                        </tr>
                      ) : (
                        companies.map((company) => (
                          <tr key={company.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="p-3 sm:p-4 font-medium break-words">
                              <span className="inline-flex items-center gap-2 flex-wrap">
                                {company.name}
                                {company.domain === ADMIN_DOMAIN && (
                                  <span className="text-xs font-bold px-2 py-0.5 bg-gray-800 text-white rounded">Admin</span>
                                )}
                              </span>
                            </td>
                            <td className="p-3 sm:p-4 break-words">{company.domain}</td>
                            <td className="p-3 sm:p-4 text-gray-700 font-medium">
                              {companyUserCounts[company.id] ?? 0}
                            </td>
                            <td className="p-3 sm:p-4">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                company.status === 'active' ? 'bg-green-100 text-green-800' :
                                company.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {company.status}
                              </span>
                            </td>
                            <td className="p-3 sm:p-4 text-gray-500 hidden lg:table-cell">
                              {new Date(company.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-3 sm:p-4">
                              <div className="flex flex-wrap gap-3">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCompanyUsersCompanyId(company.id)
                                    setActiveTab('company-users')
                                  }}
                                  className="text-sm font-medium text-blue-600 hover:underline"
                                >
                                  View users
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedCompanyId(company.id)
                                    setCompanySettingsTab('theme')
                                    setActiveTab('company-settings')
                                  }}
                                  className="text-sm font-medium text-blue-600 hover:underline"
                                >
                                  Settings
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'company-users' && (
          <CompanyUsersPanel
            initialCompanyId={companyUsersCompanyId || companies[0]?.id || null}
            onCompanyChange={(id) => setCompanyUsersCompanyId(id)}
          />
        )}

        {/* Company Settings Tab */}
        {activeTab === 'company-settings' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Company Selector */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="p-4 sm:p-6 border-b border-gray-200">
                <h2 className="text-lg sm:text-xl font-bold">
                  <i className="fa-solid fa-cog mr-2"></i>Settings
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Customize company-specific settings including theme, content visibility, and feature flags.
                </p>
              </div>
              <div className="p-4 sm:p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Company:
                </label>
                <select
                  value={selectedCompanyId || ''}
                  onChange={(e) => {
                    const newCompanyId = e.target.value || null
                    setSelectedCompanyId(newCompanyId)
                    // Reset to theme tab when company changes
                    setCompanySettingsTab('theme')
                  }}
                  className="w-full sm:w-auto min-w-[300px] p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:outline-none"
                >
                  <option value="">Select a company...</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.domain})
                    </option>
                  ))}
                </select>
                {selectedCompanyId && displayCompany && (
                  <p className="text-sm text-gray-500 mt-2">
                    Configuring: <strong>{displayCompany.name}</strong> ({displayCompany.domain})
                  </p>
                )}
              </div>
            </div>

            {/* Company Settings Sections */}
            {selectedCompanyId ? (
              <>
                {!displayCompany ? (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-center text-gray-500">
                    <p>Loading company information...</p>
                  </div>
                ) : (
                  <>
                    {/* Company Settings Tabs */}
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                      <div className="border-b border-gray-200">
                        <div className="flex gap-2 px-4 sm:px-6 pt-4">
                          <button
                            onClick={() => setCompanySettingsTab('theme')}
                            className={`px-4 py-2 font-semibold text-sm ${
                              companySettingsTab === 'theme'
                                ? 'border-b-2 border-black text-black'
                                : 'text-gray-500 hover:text-black'
                            }`}
                          >
                            Theme
                          </button>
                          <button
                            onClick={() => setCompanySettingsTab('content')}
                            className={`px-4 py-2 font-semibold text-sm ${
                              companySettingsTab === 'content'
                                ? 'border-b-2 border-black text-black'
                                : 'text-gray-500 hover:text-black'
                            }`}
                          >
                            Content
                          </button>
                          <button
                            onClick={() => setCompanySettingsTab('features')}
                            className={`px-4 py-2 font-semibold text-sm ${
                              companySettingsTab === 'features'
                                ? 'border-b-2 border-black text-black'
                                : 'text-gray-500 hover:text-black'
                            }`}
                          >
                            Features
                          </button>
                        </div>
                      </div>

                      {/* Theme Tab */}
                      {companySettingsTab === 'theme' && (
                        <div className="p-4 sm:p-6 space-y-6">
                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Primary Color</label>
                            <div className="flex gap-4 items-center">
                              <input
                                type="color"
                                value={primaryColor}
                                onChange={(e) => setPrimaryColor(e.target.value)}
                                className="w-20 h-10 border border-gray-300 rounded cursor-pointer"
                              />
                              <input
                                type="text"
                                value={primaryColor}
                                onChange={(e) => setPrimaryColor(e.target.value)}
                                className="flex-1 p-2 border border-gray-300 rounded"
                                placeholder="#000000"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Secondary Color</label>
                            <div className="flex gap-4 items-center">
                              <input
                                type="color"
                                value={secondaryColor}
                                onChange={(e) => setSecondaryColor(e.target.value)}
                                className="w-20 h-10 border border-gray-300 rounded cursor-pointer"
                              />
                              <input
                                type="text"
                                value={secondaryColor}
                                onChange={(e) => setSecondaryColor(e.target.value)}
                                className="flex-1 p-2 border border-gray-300 rounded"
                                placeholder="#0066CC"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Logo</label>
                            <p className="text-sm text-gray-500 mb-2">Predefined or upload a company-specific logo.</p>
                            <div className="flex flex-wrap gap-4 items-start">
                              <select
                                value={isPredefinedLogo(companyLogo) ? companyLogo : ''}
                                onChange={(e) => {
                                  const v = e.target.value
                                  if (v) setCompanyLogo(v)
                                }}
                                className="p-2 border border-gray-300 rounded"
                              >
                                <option value="">{companyLogo.startsWith('http') ? 'Uploaded logo' : '—'}</option>
                                <option value={LOGO_OPTIONS.LOGO1}>Logo 1</option>
                                <option value={LOGO_OPTIONS.LOGO2}>Logo 2</option>
                              </select>
                              <div className="flex flex-col gap-2">
                                <input
                                  id="company-logo-upload-input"
                                  type="file"
                                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                                  onChange={handleCompanyLogoFileSelect}
                                  className="block text-sm text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white"
                                />
                                <button
                                  type="button"
                                  onClick={handleUploadCompanyLogo}
                                  disabled={!companyLogoFile || logoUploading}
                                  className="px-3 py-1.5 bg-gray-800 text-white text-sm rounded hover:bg-gray-700 disabled:opacity-50"
                                >
                                  {logoUploading ? 'Uploading...' : 'Upload logo'}
                                </button>
                              </div>
                            </div>
                            {logoError && (
                              <p className="mt-2 text-sm text-red-600" role="alert">
                                {logoError}
                                {logoError.toLowerCase().includes('bucket') && (
                                  <span className="block mt-1 text-gray-600">
                                    Create the &quot;logos&quot; bucket in Supabase Dashboard (Storage → New bucket, name: logos, Public: Yes) or run migrations: <code className="text-xs bg-gray-100 px-1 rounded">npx supabase db push</code>.
                                  </span>
                                )}
                              </p>
                            )}
                            <div className="mt-3 flex items-center gap-4">
                              <span className="text-sm font-medium text-gray-700">Current:</span>
                              <img
                                src={companyLogoDisplayUrl}
                                alt="Company logo"
                                className="h-10 w-auto max-w-[200px] object-contain border border-gray-200 rounded"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/assets/logo1.png'
                                }}
                              />
                            </div>
                            {companyLogoPreviewUrl && (
                              <div className="mt-2 flex items-center gap-4">
                                <span className="text-sm text-gray-500">Preview:</span>
                                <img src={companyLogoPreviewUrl} alt="Preview" className="h-10 w-auto max-w-[200px] object-contain border border-gray-200 rounded" />
                              </div>
                            )}
                          </div>

                          <button
                            onClick={handleSaveTheme}
                            disabled={updateCompanySettingsMutation.isPending}
                            className="w-full py-3 bg-black text-white rounded-full font-bold hover:bg-gray-800 disabled:opacity-50"
                          >
                            {updateCompanySettingsMutation.isPending ? 'Saving...' : 'Save Theme Settings'}
                          </button>
                        </div>
                      )}

                      {/* Content Tab */}
                      {companySettingsTab === 'content' && (
                        <div className="p-4 sm:p-6 space-y-4">
                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-4">Sections</label>
                            <div className="space-y-3">
                              {['about', 'stack', 'industries', 'faq', 'newsletter', 'contact'].map((sectionId) => (
                                <div key={sectionId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      checked={sections[sectionId]?.enabled !== false}
                                      onChange={() => toggleSection(sectionId)}
                                      className="w-4 h-4"
                                    />
                                    <span className="font-medium capitalize">{sectionId}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <button
                            onClick={handleSaveContent}
                            disabled={updateCompanySettingsMutation.isPending}
                            className="w-full py-3 bg-black text-white rounded-full font-bold hover:bg-gray-800 disabled:opacity-50"
                          >
                            {updateCompanySettingsMutation.isPending ? 'Saving...' : 'Save Content Settings'}
                          </button>
                        </div>
                      )}

                      {/* Features Tab */}
                      {companySettingsTab === 'features' && (
                        <div className="p-4 sm:p-6 space-y-4">
                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Feature flags</label>
                            <p className="text-sm text-gray-500 mb-4">
                              Platform Admin dashboard is limited to @thesimpleintel.com operators; it is not a per-company product flag.
                            </p>
                            <div className="space-y-3">
                              {Object.entries(features)
                                .filter(([featureId]) => featureId !== 'adminDashboard')
                                .map(([featureId, enabled]) => (
                                <div key={featureId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      checked={enabled}
                                      onChange={() => toggleFeature(featureId)}
                                      className="w-4 h-4"
                                    />
                                    <span className="font-medium capitalize">{featureId.replace(/([A-Z])/g, ' $1').trim()}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {features.earlySignals && (
                            <div className="border-t border-gray-200 pt-4 space-y-3">
                              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                <p className="text-sm text-amber-950">
                                  This enables the Early Signals product for the company. To choose which suppliers they see, open{' '}
                                  <strong>Early Signals data → Suppliers</strong>, edit a supplier, and set company visibility.
                                </p>
                                <button
                                  type="button"
                                  onClick={() => setActiveTab('es-suppliers')}
                                  className="mt-2 text-sm font-semibold text-blue-700 hover:underline"
                                >
                                  Open Suppliers
                                </button>
                              </div>
                              <label className="block text-sm font-bold text-gray-700 mb-3">Early Signals – visible tabs</label>
                              <p className="text-sm text-gray-500 mb-3">Choose which tabs appear in the Early Signals dashboard.</p>
                              <div className="space-y-2">
                                {[
                                  { id: 'ai', label: 'Risk analyst' },
                                  { id: 'briefs', label: 'Briefs' },
                                  { id: 'executiveSummary', label: 'Risk overview' },
                                  { id: 'dashboard', label: 'Risk & mitigation monitor' },
                                  { id: 'riskMatrix', label: 'Intelligence view' },
                                  { id: 'mitigation', label: 'Mitigation tracker' },
                                  { id: 'specialReports', label: 'Special reports' },
                                  { id: 'supplierRiskIntake', label: 'Risk intake' },
                                  { id: 'riskAlerts', label: 'Risk alerts inbox' },
                                  { id: 'sendRiskAlert', label: 'Send risk alert' },
                                ].map(({ id, label }) => (
                                  <div key={id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <span className="font-medium">{label}</span>
                                    <input
                                      type="checkbox"
                                      checked={
                                        id === 'supplierRiskIntake' || id === 'sendRiskAlert' || id === 'briefs'
                                          ? !!earlySignalsTabs[id]
                                          : earlySignalsTabs[id] !== false
                                      }
                                      onChange={() => toggleEarlySignalsTab(id)}
                                      className="w-4 h-4"
                                    />
                                  </div>
                                ))}
                              </div>

                              <div className="border-t border-gray-200 pt-4">
                                <SupplierRiskEmailSettingsFields
                                  value={supplierRiskEmails}
                                  onChange={setSupplierRiskEmails}
                                />
                              </div>
                            </div>
                          )}

                          <button
                            onClick={handleSaveFeatures}
                            disabled={updateCompanySettingsMutation.isPending}
                            className="w-full py-3 bg-black text-white rounded-full font-bold hover:bg-gray-800 disabled:opacity-50"
                          >
                            {updateCompanySettingsMutation.isPending ? 'Saving...' : 'Save Feature Settings'}
                          </button>
                        </div>
                      )}

                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-center text-gray-500">
                <p>Select a company from the dropdown above to configure its settings.</p>
              </div>
            )}
          </div>
        )}

        {/* Platform Settings Tab */}
        {activeTab === 'platform-settings' && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-bold">
                <i className="fa-solid fa-image mr-2"></i>Site logos
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Platform-wide Simple AI logo for the public site. Per-company logos live under Tenants → Company settings → Theme.
              </p>
            </div>
            <div className="p-4 sm:p-6 space-y-6">
              {/* Current Logo Display */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Current Logo:</p>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 inline-block">
                  <img
                    src={logo}
                    alt="Current logo"
                    className="h-12 w-auto max-w-xs"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.src = `/assets/${LOGO_OPTIONS.LOGO1}`
                    }}
                  />
                </div>
              </div>

              {/* Predefined Logos */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Predefined Logos:</p>
                <div className="space-y-3">
                  <label className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                    <input
                      type="radio"
                      name="logo"
                      value={LOGO_OPTIONS.LOGO1}
                      checked={currentLogo === LOGO_OPTIONS.LOGO1}
                      onChange={() => switchLogo(LOGO_OPTIONS.LOGO1)}
                      className="mr-4"
                    />
                    <div className="flex items-center gap-4 flex-grow">
                      <span className="font-medium">Logo 1</span>
                      <img
                        src="/assets/logo1.png"
                        alt="Logo 1"
                        className="h-10 w-auto ml-auto"
                      />
                    </div>
                  </label>

                  <label className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                    <input
                      type="radio"
                      name="logo"
                      value={LOGO_OPTIONS.LOGO2}
                      checked={currentLogo === LOGO_OPTIONS.LOGO2}
                      onChange={() => switchLogo(LOGO_OPTIONS.LOGO2)}
                      className="mr-4"
                    />
                    <div className="flex items-center gap-4 flex-grow">
                      <span className="font-medium">Logo 2</span>
                      <img
                        src="/assets/logo2.png"
                        alt="Logo 2"
                        className="h-10 w-auto ml-auto"
                      />
                    </div>
                  </label>
                </div>
              </div>

              {/* Upload New Logo */}
              <div className="border-t border-gray-200 pt-6">
                <p className="text-sm font-medium text-gray-700 mb-3">Upload New Logo:</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Image (PNG, JPG, SVG, WebP - Max 2MB)
                    </label>
                    <input
                      id="logo-upload-input"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      onChange={handleFileSelect}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800 cursor-pointer"
                    />
                  </div>

                  {previewUrl && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 inline-block">
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="h-16 w-auto max-w-xs"
                        />
                      </div>
                    </div>
                  )}

                  {logoError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                      {logoError}
                    </div>
                  )}

                  <button
                    onClick={handleUpload}
                    disabled={!selectedFile || logoUploading}
                    className="w-full sm:w-auto px-6 py-2 bg-black text-white rounded-md font-bold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {logoUploading ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2"></i>Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-upload mr-2"></i>Upload Logo
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Uploaded Logos */}
              {uploadedLogos.length > 0 && (
                <div className="border-t border-gray-200 pt-6">
                  <p className="text-sm font-medium text-gray-700 mb-3">Uploaded Logos:</p>
                  <div className="space-y-3">
                    {uploadedLogos.map((logoUrl, index) => (
                      <div
                        key={index}
                        className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <label className="flex items-center gap-4 flex-grow cursor-pointer">
                          <input
                            type="radio"
                            name="logo"
                            value={logoUrl}
                            checked={currentLogo === logoUrl}
                            onChange={() => switchLogo(logoUrl)}
                            className="mr-2"
                          />
                          <img
                            src={logoUrl}
                            alt={`Uploaded logo ${index + 1}`}
                            className="h-10 w-auto"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                            }}
                          />
                          <span className="text-sm text-gray-600 ml-2 truncate flex-1">
                            Uploaded Logo {index + 1}
                          </span>
                        </label>
                        <button
                          onClick={() => handleDeleteLogo(logoUrl)}
                          className="ml-4 text-red-500 hover:text-red-700 p-2"
                          aria-label="Delete logo"
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Early Signals data sections */}
        {(activeTab === 'es-suppliers' || activeTab === 'es-import' || activeTab === 'es-reports' || activeTab === 'es-spend') && (
          <EarlySignalsPanel
            section={ES_TAB_TO_SECTION[activeTab]}
            onNavigateSection={(next) => setActiveTab(SECTION_TO_ES_TAB[next])}
            onOpenCompanySettings={() => {
              setCompanySettingsTab('features')
              setActiveTab('company-settings')
            }}
          />
        )}

        {activeTab === 'es-data-api' && <DataApiPanel />}

        {activeTab === 'platform-sso' && <SsoPanel />}

        {/* AI Settings Tab */}
        {activeTab === 'ai-settings' && (
          <AISettingsPanel />
        )}

        {/* News API Tab */}
        {activeTab === 'news-api' && (
          <NewsApiPanel />
        )}
          </div>
        </div>
      </main>
    </div>
  )
}

export default AdminDashboard
